if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.sectionId;
        this.mainButton = document.getElementById(`ProductSubmit-${sectionId}`);
        this.mainPrice = document.getElementById(`price-${sectionId}`);
        this.stickyButton = this.querySelector('.sticky-atc__button');
        this.stickyLabel = this.querySelector('.sticky-atc__button-label');
        this.stickySpinner = this.stickyButton?.querySelector('.loading-overlay__spinner');
        this.stickyPrice = this.querySelector('.sticky-atc__price');

        // Without the main buy box there is nothing to mirror or anchor to.
        if (!this.mainButton) return;

        // The bar re-renders the price snippet, whose markup carries ids that already
        // exist in the main price block; drop them so the DOM has no duplicate ids.
        this.stripPriceIds();

        this.initVisibility();
        this.initPriceSync();
        this.initSizeGuard();
        this.initButtonSync();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.buttonObserver?.disconnect();
        if (this.onStickyClick) this.stickyButton?.removeEventListener('click', this.onStickyClick);
        if (this.onSizeEngaged && this.sizeField) {
          ['change', 'pointerdown', 'keydown'].forEach((evt) =>
            this.sizeField.removeEventListener(evt, this.onSizeEngaged)
          );
        }
        clearTimeout(this.highlightTimer);
        document.body.classList.remove('sticky-atc-visible');
      }

      // A single switch for the bar: drives the slide-in and the body class that
      // offsets page content and the chat launcher while the bar is on screen.
      setVisible(visible) {
        if (visible) this.measureHeight();
        this.hidden = !visible;
        document.body.classList.toggle('sticky-atc-visible', visible);
      }

      // Publish the bar's real height (incl. safe-area padding) so the page offset
      // and the lifted chat launcher track it exactly instead of a guessed value.
      measureHeight() {
        const height = this.offsetHeight;
        if (height > 0) document.body.style.setProperty('--sticky-atc-height', `${height}px`);
      }

      stripPriceIds() {
        if (!this.stickyPrice) return;
        this.stickyPrice.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      }

      // Reveal the bar only once the main add-to-cart button is out of view.
      initVisibility() {
        if (!('IntersectionObserver' in window)) {
          this.setVisible(true);
          return;
        }
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            this.setVisible(!entries[0].isIntersecting);
          },
          { rootMargin: '0px 0px -10% 0px' }
        );
        this.visibilityObserver.observe(this.mainButton);
      }

      // The main price container is re-rendered on variant change; mirror it.
      initPriceSync() {
        if (!this.mainPrice || !this.stickyPrice) return;
        this.priceObserver = new MutationObserver(() => {
          this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
          this.stripPriceIds();
        });
        this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true });
      }

      // The first available variant is pre-selected, so a tap would otherwise add
      // a size the shopper never chose. Until they engage the size control, the bar
      // reads "Select your size" and a tap sends them to it instead of adding.
      // Guards only a real SIZE choice (the fit/returns risk) — not colour/other
      // options, where the copy would be wrong and the pre-selected value is fine.
      // If no such control is found the guard stays off and the bar adds as before
      // (fail open — never blocks a legitimate add).
      initSizeGuard() {
        const sectionId = this.dataset.sectionId;
        const picker =
          document.querySelector(
            `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`
          ) ||
          this.mainButton
            .closest('product-info, .product, section')
            ?.querySelector('variant-selects, variant-radios');

        // Match the theme's own size-option names; storefront is English-only.
        const sizeNames = ['size', 'taille', 'größe'];
        const isSize = (name) => sizeNames.includes((name || '').trim().toLowerCase());

        let count = 0;
        if (picker) {
          const select = Array.from(picker.querySelectorAll('select')).find((s) => {
            const match = s.name.match(/^options\[(.+)\]$/i);
            return match && isSize(match[1]);
          });
          if (select) {
            this.sizeControl = select;
            count = select.options.length;
          } else {
            const fieldset = Array.from(picker.querySelectorAll('fieldset')).find((fs) =>
              isSize(fs.querySelector('legend')?.textContent)
            );
            if (fieldset) {
              this.sizeControl = fieldset.querySelector('input[type="radio"]');
              count = fieldset.querySelectorAll('input[type="radio"]').length;
            }
          }
        }

        this.guardActive = Boolean(this.sizeControl) && count > 1;
        this.sizeChosen = !this.guardActive;
        if (!this.guardActive) return;

        this.sizeField = this.sizeControl.closest('.product-form__input') || this.sizeControl;

        // Opening (pointerdown/keydown) or changing the size control all count as
        // the shopper engaging with size — opening matters because someone who wants
        // the pre-selected size can't fire a change by re-picking the same value.
        this.onSizeEngaged = () => {
          if (this.sizeChosen) return;
          this.sizeChosen = true;
          this.syncButton?.();
        };
        ['change', 'pointerdown', 'keydown'].forEach((evt) =>
          this.sizeField.addEventListener(evt, this.onSizeEngaged)
        );

        this.onStickyClick = (event) => {
          if (this.sizeChosen) return;
          event.preventDefault();
          this.promptForSize();
        };
        this.stickyButton.addEventListener('click', this.onStickyClick);
      }

      promptForSize() {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.sizeField.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        this.sizeControl.focus({ preventScroll: true });

        this.sizeField.classList.add('sticky-atc-size-highlight');
        clearTimeout(this.highlightTimer);
        this.highlightTimer = setTimeout(
          () => this.sizeField.classList.remove('sticky-atc-size-highlight'),
          2000
        );
      }

      // Keep availability, label and loading state in step with the main button
      // (sold out, in-flight) so the two buttons never disagree. Before a size is
      // chosen the bar instead shows the "Select your size" CTA (kept enabled even
      // if the pre-selected variant is sold out, since another size may be in stock).
      initButtonSync() {
        if (!this.stickyButton) return;
        const sync = () => {
          if (this.guardActive && !this.sizeChosen) {
            this.stickyButton.disabled = false;
            this.stickyButton.classList.add('sticky-atc__button--needs-size');
            this.stickyButton.classList.remove('loading');
            this.stickySpinner?.classList.add('hidden');
            if (this.stickyLabel && this.dataset.selectSizeText) {
              this.stickyLabel.textContent = this.dataset.selectSizeText;
            }
            return;
          }
          this.stickyButton.classList.remove('sticky-atc__button--needs-size');

          const disabled =
            this.mainButton.hasAttribute('disabled') ||
            this.mainButton.getAttribute('aria-disabled') === 'true';
          this.stickyButton.disabled = disabled;

          const label = this.mainButton.querySelector('span');
          if (label && this.stickyLabel) this.stickyLabel.textContent = label.textContent.trim();

          const loading = this.mainButton.classList.contains('loading');
          this.stickyButton.classList.toggle('loading', loading);
          this.stickySpinner?.classList.toggle('hidden', !loading);
        };
        this.syncButton = sync;
        sync();
        this.buttonObserver = new MutationObserver(sync);
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }
    }
  );
}
